import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  Search,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteShell } from "@/components/site/SiteShell";
import {
  QuoteCovers,
  QuoteDisclaimer,
  QuoteTotals,
} from "@/components/quote/QuoteBreakdown";
import { api, messageFrom } from "@/lib/api";
import { formatDate } from "@/lib/format";

export default function VerifyQuotePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reference, setReference] = useState(searchParams.get("ref") || "");
  const [state, setState] = useState("idle"); // idle | loading | found | missing
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);

  const lookup = useCallback(
    async (ref) => {
      const trimmed = String(ref || "").trim();
      if (!trimmed) {
        setError("Please enter the quote reference printed on your PDF.");
        return;
      }
      setError(null);
      setState("loading");
      try {
        const data = await api.getQuote(trimmed.toUpperCase());
        setQuote(data);
        setState("found");
      } catch (err) {
        setQuote(null);
        setState("missing");
        setError(
          messageFrom(
            err,
            "We couldn't find that quote reference. Please check the characters and try again.",
          ),
        );
      }
    },
    [],
  );

  // Look up whatever reference the link carries, and look again if the link
  // changes underneath us (a second QR code scanned into the same tab).
  const linkedRef = searchParams.get("ref");
  useEffect(() => {
    if (linkedRef) lookup(linkedRef);
  }, [linkedRef, lookup]);

  const submit = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (reference.trim()) next.set("ref", reference.trim().toUpperCase());
    setSearchParams(next, { replace: true });
    lookup(reference);
  };

  return (
    <SiteShell testId="verify-quote-page">
      <section className="relative overflow-hidden">
        <div className="tt-hero-wash absolute inset-x-0 top-0 h-[260px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-4 pb-8 pt-12 sm:px-6 sm:pt-16">
          <span className="tt-stamp">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Quote verification
          </span>
          <h1 className="mt-5 text-[32px] font-extrabold leading-tight text-foreground sm:text-[44px]">
            Verify a T-TITE quote
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Every quote we generate is stored on our server against its reference number. Enter
            the reference from your PDF (or scan its QR code) to see the original figures exactly
            as we issued them. If a document has been altered, it won't match what appears here.
          </p>

          <form onSubmit={submit} className="tt-card mt-8 p-5 sm:p-6">
            <Label
              htmlFor="verify-ref"
              className="text-xs font-bold uppercase tracking-[0.12em] text-primary"
            >
              Quote reference
            </Label>
            <div className="mt-2 flex flex-col gap-3 sm:flex-row">
              <Input
                id="verify-ref"
                data-testid="verify-quote-reference-input"
                value={reference}
                onChange={(event) => {
                  setReference(event.target.value.toUpperCase());
                  if (error) setError(null);
                }}
                placeholder="TT-202606-4F9B64A1"
                className="h-12 font-amount text-base tracking-wide"
                aria-invalid={state === "missing" ? "true" : "false"}
              />
              <Button
                type="submit"
                data-testid="verify-quote-submit-button"
                disabled={state === "loading"}
                className="h-12 shrink-0 px-6 text-base font-bold sm:min-w-[170px]"
              >
                {state === "loading" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Search className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                Verify quote
              </Button>
            </div>
            {error && state !== "missing" ? (
              <p role="alert" className="mt-2 text-[13px] font-medium text-destructive">
                {error}
              </p>
            ) : null}
          </form>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-4 sm:px-6">
        {state === "missing" ? (
          <div
            data-testid="verify-quote-not-found"
            role="alert"
            className="tt-card tt-fade-in p-6"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <XCircle className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-xl font-bold text-foreground">
              We couldn't find that reference
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {error}
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              <li>• References look like <span className="font-amount font-semibold">TT-202606-4F9B64A1</span>.</li>
              <li>• Check for 0 (zero) versus O, and 1 versus I.</li>
              <li>
                • Still no luck?{" "}
                <a href="mailto:info@t-tite.co.za" className="tt-link">
                  Email us
                </a>{" "}
                or call{" "}
                <a href="tel:+27817882993" className="tt-link">
                  081 788 2993
                </a>
                .
              </li>
            </ul>
          </div>
        ) : null}

        {state === "found" && quote ? (
          <div data-testid="verify-quote-result" className="tt-fade-in space-y-5">
            <div className="tt-card overflow-hidden">
              <div className="flex items-start gap-3 border-b-2 border-primary bg-[color:var(--tt-teal-mist)] px-5 py-5 sm:px-6">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-primary" aria-hidden="true" />
                <div>
                  <h2 className="text-lg font-bold text-foreground">
                    This quote is genuine and on our records
                  </h2>
                  <p className="mt-1 text-[13px] leading-relaxed text-[color:var(--tt-teal-deep)]">
                    Below are the figures exactly as T-TITE generated and stored them. If the PDF
                    in your hand shows anything different, it has been altered.
                  </p>
                </div>
              </div>

              <dl className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-y-0 sm:divide-x">
                <div className="px-5 py-4">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                    Quote reference
                  </dt>
                  <dd className="mt-1 font-amount text-sm font-bold text-foreground">
                    {quote.quote_ref}
                  </dd>
                </div>
                <div className="px-5 py-4">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                    Prepared for
                  </dt>
                  <dd className="mt-1 text-sm font-bold text-foreground">
                    {quote.prepared_for?.company || "\u2014"}
                    {quote.prepared_for?.full_name ? (
                      <span className="block text-xs font-normal text-muted-foreground">
                        {quote.prepared_for.full_name}
                      </span>
                    ) : null}
                  </dd>
                </div>
                <div className="border-t border-border px-5 py-4">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                    Date issued
                  </dt>
                  <dd className="mt-1 text-sm font-bold text-foreground">
                    {formatDate(quote.issued_at)}
                  </dd>
                </div>
                <div className="border-t border-border px-5 py-4">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                    Valid until
                  </dt>
                  <dd className="mt-1 text-sm font-bold text-foreground">
                    {formatDate(quote.valid_until)}
                  </dd>
                </div>
              </dl>

              <div className="border-t border-border px-5 py-5 sm:px-6">
                <p className="tt-eyebrow">Quote covers</p>
                <p className="mt-1.5 text-lg font-bold leading-snug text-foreground">
                  {quote.headline}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{quote.service_label}</p>
              </div>
            </div>

            {quote.is_expired ? (
              <div
                data-testid="verify-quote-expired"
                role="status"
                className="flex items-start gap-3 rounded-xl border p-4"
                style={{
                  borderColor: "var(--tt-warn-border)",
                  backgroundColor: "var(--tt-warn-bg)",
                }}
              >
                <AlertTriangle
                  className="mt-0.5 h-5 w-5 shrink-0"
                  style={{ color: "var(--tt-warn-text)" }}
                  aria-hidden="true"
                />
                <p className="text-[13px] leading-relaxed" style={{ color: "#4A3B10" }}>
                  <strong>This quote's {quote.validity_days}-day validity period has passed.</strong>{" "}
                  The figures below are still the authentic originals, but please get in touch for
                  an up-to-date price.
                </p>
              </div>
            ) : null}

            <QuoteTotals totals={quote.totals} />

            <div>
              <h3 className="tt-eyebrow">What this quote covers</h3>
              <div className="mt-3">
                <QuoteCovers covers={quote.covers} />
              </div>
            </div>

            <QuoteDisclaimer disclaimer={quote.disclaimer} notices={quote.notices} />

            <Button asChild variant="outline" className="h-12 bg-white">
              <a
                href={api.quotePdfUrl(quote.quote_ref)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="verify-quote-download-button"
              >
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                Download the authentic PDF
              </a>
            </Button>
          </div>
        ) : null}

        {state === "idle" ? (
          <div className="tt-card border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Enter a reference above to check a quote.
            </p>
          </div>
        ) : null}
      </section>
    </SiteShell>
  );
}
